import { ref, computed, toValue } from 'vue'
import { useWatchlistStore } from '@/stores'
import { normalizeFiltersForComparison } from '@/composables/useWatchlistFilters'

/**
 * The staged ("draft") criteria of one watchlist: what the user is editing in the aside
 * before pressing Save. Owns seeding from the saved watchlist, dirty detection, Revert,
 * the brand/presentation pin rules, and saving with the stale-write check.
 *
 * Pin rules (server enforces the same ones, this keeps the UI from ever showing an
 * invalid state): a tracked presentation implies its parent brand is tracked; a brand on its
 * own is valid; removing a brand removes its tracked presentations too.
 *
 * `isEditable` is the single switch every editing control reads. It is always true today; a
 * future start/stop ("locked while running") model turns it off here instead of in each control.
 */
// Typesense returns id fields (brand_id, presentation_id) as strings, while the REST API
// (used by BrandPresentationsPickerDialog and the seeded watchlist payload) returns them as
// numbers. Every id that enters this composable — from either source — is coerced through
// this so brands/presentations arrays never end up with the same brand tracked twice under
// two different-typed ids (which also makes unpinBrand/unpinPresentation silently miss the
// other-typed duplicate on removal).
const toId = value => value === null || value === undefined ? value : Number(value)

export const useWatchlistCriteria = ({ watchlistId, watchlist, refetchWatchlist }) => {
  const { updateWatchlist } = useWatchlistStore()
  // One mutation for every write from the page (criteria and title/description), so all of them
  // go through the same cache update of `updatedAt`.
  const { mutateAsync: putWatchlist, isPending } = updateWatchlist()

  // Which watchlist the staged criteria were seeded from — not a plain "seeded once" flag,
  // because the page instance is reused when only :watchlistId changes (e.g. a toast "View"
  // click while another watchlist is open), and stale criteria must never carry over.
  const seededWatchlistId = ref(null)
  // Bumped by Revert / Reload to remount whatever reads `filters` once at construction.
  const seedNonce = ref(0)

  const brands = ref([])
  const presentations = ref([])
  const favGroups = ref([])
  const filters = ref(null)
  const notifyInApp = ref(true)
  const notifyEmail = ref(false)
  const savedSnapshot = ref(null)
  const isConflictOpen = ref(false)
  const isSavingDetails = ref(false)

  const isSaving = computed(() => isPending.value && !isSavingDetails.value)

  const isEditable = computed(() => true)
  const isSeeded = computed(() => seededWatchlistId.value !== null && String(seededWatchlistId.value) === String(toValue(watchlistId)))

  // filters is run through normalizeFiltersForComparison because its actual key shape is
  // unstable — FilterCloud's CurrentRefinements widget echoes it back via v-model using the raw
  // Typesense field name (snake_case), while the watchlist API returns/expects camelCase —
  // same filters, two different-looking shapes.
  const snapshot = computed(() => JSON.stringify({
    brands: brands.value.map(brand => brand.brand_id),
    presentations: presentations.value.map(presentation => presentation.presentation_id),
    favGroups: favGroups.value.map(group => group.id),
    filters: normalizeFiltersForComparison(filters.value),
    notifyInApp: notifyInApp.value,
    notifyEmail: notifyEmail.value
  }))
  const isDirty = computed(() => savedSnapshot.value !== null && snapshot.value !== savedSnapshot.value)

  // Shapes the tracked-items grid (BrandPresentationsGrid) expects.
  const brandListItems = computed(() => brands.value.map(brand => ({ ...brand, brandId: brand.brand_id })))
  const presentationListItems = computed(() => presentations.value.map(presentation => ({
    ...presentation,
    key: presentation.presentation_id,
    brandId: presentation.brand_id,
    presentationId: presentation.presentation_id,
    strength: presentation.strength,
    pharmaceuticalForm: presentation.pharmaceutical_form
  })))

  const toPayload = () => ({
    brand_ids: brands.value.map(brand => brand.brand_id),
    presentation_ids: presentations.value.map(presentation => presentation.presentation_id),
    fav_group_ids: favGroups.value.map(group => group.id),
    notify_in_app: notifyInApp.value,
    notify_email: notifyEmail.value,
    filters: filters.value
  })

  const seed = payload => {
    brands.value = (payload.pinnedBrands ?? []).map(brand => ({ brand_id: toId(brand.brandId), title: brand.title, subtitle: brand.subtitle }))
    presentations.value = (payload.pinnedPresentations ?? []).map(presentation => ({
      presentation_id: toId(presentation.presentationId),
      brand_id: toId(presentation.brandId),
      strength: presentation.strength,
      pharmaceutical_form: presentation.pharmaceuticalForm
    }))
    favGroups.value = [...(payload.favGroups ?? [])]
    filters.value = payload.filters ?? null
    notifyInApp.value = payload.notifyInApp
    notifyEmail.value = payload.notifyEmail
    savedSnapshot.value = snapshot.value
    seededWatchlistId.value = payload.id
  }

  const revert = () => {
    if (!toValue(watchlist)) return
    seed(toValue(watchlist))
    seedNonce.value++
  }

  const presentationCountFor = brandId => {
    const id = toId(brandId)
    return presentations.value.filter(presentation => presentation.brand_id === id).length
  }

  const pinBrand = brand => {
    const brandId = toId(brand.brand_id)
    if (brands.value.some(item => item.brand_id === brandId)) return
    brands.value.push({ brand_id: brandId, title: brand.title, subtitle: brand.subtitle })
  }
  // A tracked presentation can't outlive its brand, so removing the brand takes its
  // tracked presentations with it.
  const unpinBrand = brandId => {
    const id = toId(brandId)
    brands.value = brands.value.filter(brand => brand.brand_id !== id)
    presentations.value = presentations.value.filter(presentation => presentation.brand_id !== id)
  }
  const pinPresentation = presentation => {
    const presentationId = toId(presentation.presentation_id)
    const brandId = toId(presentation.brand_id)
    if (!presentations.value.some(item => item.presentation_id === presentationId)) {
      presentations.value.push({
        presentation_id: presentationId,
        brand_id: brandId,
        strength: presentation.strength,
        pharmaceutical_form: presentation.pharmaceutical_form
      })
    }
    if (brandId && !brands.value.some(brand => brand.brand_id === brandId)) {
      brands.value.push({ brand_id: brandId, title: presentation.brand_title ?? `#${brandId}` })
    }
  }
  const unpinPresentation = presentationId => {
    const id = toId(presentationId)
    presentations.value = presentations.value.filter(presentation => presentation.presentation_id !== id)
  }
  const linkFavGroup = group => {
    if (!favGroups.value.some(item => item.id === group.id)) favGroups.value.push(group)
  }
  const unlinkFavGroup = group => {
    favGroups.value = favGroups.value.filter(item => item.id !== group.id)
  }

  /**
   * Saves the draft. Sends the `updatedAt` last seen as `expectedUpdatedAt`, so a save from a
   * stale page/tab is refused (409) instead of silently undoing someone else's edit; on
   * conflict the conflict dialog opens and the error is rethrown so callers (e.g. the
   * unsaved-changes guard) know the save did not happen. `overwrite` skips the check.
   */
  const save = async ({ overwrite = false } = {}) => {
    try {
      const { data } = await putWatchlist({
        id: toValue(watchlistId),
        ...toPayload(),
        ...(overwrite ? {} : { expected_updated_at: toValue(watchlist)?.updatedAt })
      })
      // Re-seed from what the server actually stored (it may have added parent brands).
      seed(data)
      isConflictOpen.value = false
    } catch (error) {
      if (error.response?.status === 409) isConflictOpen.value = true
      throw error
    }
  }
  const isConflict = error => error?.response?.status === 409

  const reloadLatest = async () => {
    const { data } = await refetchWatchlist()
    if (data) {
      seed(data)
      seedNonce.value++
    }
    isConflictOpen.value = false
  }
  const overwrite = () => save({ overwrite: true })

  // Title and description save immediately (inline edit), outside the staged draft and its
  // stale-write check; the draft is untouched.
  const saveDetails = async ({ title, description }) => {
    isSavingDetails.value = true
    try {
      await putWatchlist({ id: toValue(watchlistId), title, description })
    } finally {
      isSavingDetails.value = false
    }
  }

  return {
    brands,
    presentations,
    favGroups,
    filters,
    notifyInApp,
    notifyEmail,
    seededWatchlistId,
    seedNonce,
    isSeeded,
    isEditable,
    isDirty,
    isSaving,
    isSavingDetails,
    isConflictOpen,
    brandListItems,
    presentationListItems,
    presentationCountFor,
    seed,
    revert,
    save,
    isConflict,
    reloadLatest,
    overwrite,
    saveDetails,
    pinBrand,
    unpinBrand,
    pinPresentation,
    unpinPresentation,
    linkFavGroup,
    unlinkFavGroup
  }
}
